/**
 * Client-side lifecycle simulator for CARF mock submissions.
 *
 * Triggered by TransmitCarfTransmission resolver. Schedules a setTimeout
 * that advances a submission from `submitted` → `accepted`.
 *
 * The polling hook (usePolledList) picks up the change on its next tick.
 *
 * HANDOFF NOTE: delete this file when the real backend ships. Remove the
 * import from mockResolvers.js and the scheduleLifecycleAdvancement call.
 */

import {
  appendSubmissionEvent,
  findTransmission,
  updateTransmission,
} from './mockStore';

const supersedeParent = (transmissionId) => {
  const tx = findTransmission(transmissionId);
  if (!tx?.parentTransmissionId) return;
  updateTransmission(tx.parentTransmissionId, { status: 'superseded' });
  appendSubmissionEvent({
    transmissionId: tx.parentTransmissionId,
    kind: 'superseded',
    message: `Superseded by correction ${tx.messageRefId || transmissionId}`,
  });
};

const STAGES = [
  {
    toStatus: 'accepted',
    delayMs: 16000,
    eventKind: 'accepted',
    message: 'Authority accepted all records',
  },
];

// transmissionId → array of pending timeout handles
const pendingTimers = new Map();

const applyStage = (transmissionId, stage) => {
  updateTransmission(transmissionId, { status: stage.toStatus });
  appendSubmissionEvent({
    transmissionId,
    kind: stage.eventKind,
    message: stage.message,
  });
  if (stage.toStatus === 'accepted') {
    supersedeParent(transmissionId);
    pendingTimers.delete(transmissionId);
  }
};

/**
 * Schedule lifecycle advancement from `submitted` → `accepted`.
 * Idempotent — calling twice for the same transmissionId is a no-op.
 */
export const scheduleLifecycleAdvancement = (transmissionId) => {
  if (pendingTimers.has(transmissionId)) return;
  const timers = STAGES.map((stage) =>
    setTimeout(() => applyStage(transmissionId, stage), stage.delayMs),
  );
  pendingTimers.set(transmissionId, timers);
};
